import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const API = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,  // Envoyer le cookie httpOnly automatiquement
});

// Handle 401 responses globally
// Silent endpoints: 401 is surfaced to the caller but must NOT force-redirect
// (background polling would otherwise kick the user out mid-operation).
const SILENT_401_URLS = ['/auth/me', '/notifications/unread-count'];
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isSilent = SILENT_401_URLS.some((u) => url.includes(u));
    if (error.response?.status === 401 && !isSilent) {
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// --- Search ---
export const globalSearch = (q) => API.get('/search', { params: { q } });

// --- Dashboard ---
export const getDashboardStats = () => API.get('/dashboard/stats');

// --- Projects ---
export const getProjects = () => API.get('/projects');
export const getProject = (id) => API.get(`/projects/${id}`);
export const createProject = (data) => API.post('/projects', data);
export const updateProject = (id, data) => API.put(`/projects/${id}`, data);
export const deleteProject = (id) => API.delete(`/projects/${id}`);
// Ce qu'une suppression emporterait — aucun effet de bord, purement informatif
export const getProjectDeletionImpact = (id) =>
  API.get(`/projects/${id}/deletion-impact`);
export const getUserDeletionImpact = (id) =>
  API.get(`/auth/users/${id}/deletion-impact`);

// --- Requirements ---
export const createRequirement = (projectId, data) =>
  API.post(`/projects/${projectId}/requirements`, data);
export const updateRequirement = (projectId, reqId, data) =>
  API.put(`/projects/${projectId}/requirements/${reqId}`, data);
export const deleteRequirement = (projectId, reqId) =>
  API.delete(`/projects/${projectId}/requirements/${reqId}`);
export const updateRequirementStatus = (projectId, reqId, statutValidation) =>
  API.patch(`/projects/${projectId}/requirements/${reqId}/status`, { statut_validation: statutValidation });

// --- Test Cases ---
export const createTestCase = (projectId, reqId, data) =>
  API.post(`/projects/${projectId}/requirements/${reqId}/test-cases`, data);
export const updateTestCase = (projectId, tcId, data) =>
  API.put(`/projects/${projectId}/test-cases/${tcId}`, data);
export const updateTestCaseStatus = (projectId, tcId, statut, commentaire, environment = 'DEV') => {
  const payload = { statut, environment };
  if (commentaire !== undefined && commentaire !== null) payload.commentaire = commentaire;
  return API.patch(`/projects/${projectId}/test-cases/${tcId}/status`, payload);
};
export const updateTestCaseComment = (projectId, tcId, commentaire) =>
  API.patch(`/projects/${projectId}/test-cases/${tcId}/comment`, { commentaire });
export const deleteTestCase = (projectId, tcId) =>
  API.delete(`/projects/${projectId}/test-cases/${tcId}`);

// --- Pieces jointes (attachments) ---
export const uploadAttachment = (projectId, tcId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post(`/projects/${projectId}/test-cases/${tcId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteAttachment = (projectId, tcId, attId) =>
  API.delete(`/projects/${projectId}/test-cases/${tcId}/attachments/${attId}`);
export const fetchAttachmentBlob = async (projectId, tcId, attId) => {
  const response = await API.get(
    `/projects/${projectId}/test-cases/${tcId}/attachments/${attId}`,
    { responseType: 'blob' }
  );
  return URL.createObjectURL(response.data);
};

// --- Fil de discussion (commentaires) ---
export const getComments = (projectId, tcId) =>
  API.get(`/projects/${projectId}/test-cases/${tcId}/comments`);
export const addComment = (projectId, tcId, message) =>
  API.post(`/projects/${projectId}/test-cases/${tcId}/comments`, { message });
export const deleteComment = (projectId, tcId, commentId) =>
  API.delete(`/projects/${projectId}/test-cases/${tcId}/comments/${commentId}`);

// --- Preview ---
export const getPreviewCahier = (projectId) =>
  API.get(`/projects/${projectId}/preview/cahier`);

// --- Export (authenticated download) ---
export const downloadExcel = (projectId, environment = 'DEV') =>
  API.get(`/projects/${projectId}/export/excel`, { params: { environment }, responseType: 'blob' });
export const downloadJson = (projectId) =>
  API.get(`/projects/${projectId}/export/json`, { responseType: 'blob' });
export const downloadPdf = (projectId, environment = 'DEV') =>
  API.get(`/projects/${projectId}/export/pdf`, { params: { environment }, responseType: 'blob' });

// --- AI Config ---
export const getAIConfigs = () => API.get('/ai-config');
export const saveAIConfig = (data) => API.post('/ai-config', data);
export const deleteAIConfig = (provider) => API.delete(`/ai-config/${provider}`);
export const updateModelConfig = (provider, data) => API.put(`/ai-config/${provider}/model`, data);
export const getAIUsageStats = () => API.get('/ai-usage');

// --- Ollama (LLM Local) ---
export const getOllamaModels = () => API.get('/ai-config/ollama/models');
export const testOllamaConnection = () => API.post('/ai-config/ollama/test');

// --- AI --- (long timeout: preview models like gemini-3 can take 5+ min)
const AI_TIMEOUT = 10 * 60 * 1000; // 10 minutes
export const aiGenerate = (projectId, data) =>
  API.post(`/projects/${projectId}/ai/generate`, data, { timeout: AI_TIMEOUT });
export const aiGenerateUpload = (projectId, formData) =>
  API.post(`/projects/${projectId}/ai/generate-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: AI_TIMEOUT,
  });
export const aiAccept = (projectId, data) =>
  API.post(`/projects/${projectId}/ai/accept`, data);

// Annonce le decoupage (feuilles Excel) avant generation — aucun appel IA
export const aiInspectUpload = (projectId, formData) =>
  API.post(`/projects/${projectId}/ai/inspect-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// --- Documents sources IA (superadmin) ---
export const listAISources = (params) =>
  API.get('/admin/ai-sources', { params });
export const listAISourceProjects = () =>
  API.get('/admin/ai-sources/projects');
export const fetchAISourceFile = (docId) =>
  API.get(`/admin/ai-sources/${docId}/file`, { responseType: 'blob' });
export const deleteAISource = (docId) =>
  API.delete(`/admin/ai-sources/${docId}`);
export const purgeAISources = () =>
  API.post('/admin/ai-sources/purge');

// --- AI Streaming (SSE) --- uses native fetch for ReadableStream support
const BASE_URL = API_BASE_URL;

export const aiGenerateStream = (projectId, data) =>
  fetch(`${BASE_URL}/projects/${projectId}/ai/generate-stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

export const aiGenerateUploadStream = (projectId, formData) =>
  fetch(`${BASE_URL}/projects/${projectId}/ai/generate-upload-stream`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

// --- AI Targeted (Enrich / Improve) --- SSE streaming
export const aiEnrichRequirementStream = (projectId, reqId, data) =>
  fetch(`${BASE_URL}/projects/${projectId}/ai/requirements/${reqId}/enrich-stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

export const aiImproveTestCaseStream = (projectId, tcId, data) =>
  fetch(`${BASE_URL}/projects/${projectId}/ai/test-cases/${tcId}/improve-stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

export const aiAcceptNewCases = (projectId, reqId, data) =>
  API.post(`/projects/${projectId}/ai/requirements/${reqId}/accept-new-cases`, data);

export const aiAcceptImprovement = (projectId, tcId, data) =>
  API.post(`/projects/${projectId}/ai/test-cases/${tcId}/accept-improvement`, data);

// --- Import ---
export const importFile = (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post(`/projects/${projectId}/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
};
export const confirmImport = (projectId, data) =>
  API.post(`/projects/${projectId}/import/confirm`, data);

// --- Reorder ---
export const reorderRequirements = (projectId, order) =>
  API.patch(`/projects/${projectId}/requirements/reorder`, { order });
export const reorderTestCases = (projectId, reqId, order) =>
  API.patch(`/projects/${projectId}/requirements/${reqId}/test-cases/reorder`, { order });

// --- Users List (for assignment dropdowns) ---
export const getUsersList = () => API.get('/auth/users-list');

// --- Audit Logs ---
export const getAuditLogs = (projectId, params = {}) =>
  API.get(`/projects/${projectId}/audit-logs`, { params });

// --- Squash TM Config ---
export const getSquashConfig = () => API.get('/squash-config');
export const saveSquashConfig = (data) => API.post('/squash-config', data);
export const deleteSquashConfig = () => API.delete('/squash-config');
export const testSquashConnection = () => API.post('/squash-config/test');

// --- Squash TM Sync Tracking ---
export const getSquashSyncLogs = (projectId) =>
  API.get('/squash-sync-logs', { params: projectId ? { project_id: projectId } : {} });
export const deleteSquashSyncLog = (logId) => API.delete(`/squash-sync-logs/${logId}`);

// --- Squash TM Sync ---
export const syncToSquash = (projectId) =>
  API.post(`/projects/${projectId}/sync/squashtm`, {}, { timeout: 5 * 60 * 1000 });

// --- Squash TM Sync unitaire (exigence ou cas de test individuel) ---
export const syncRequirementToSquash = (projectId, requirementId) =>
  API.post(`/projects/${projectId}/sync/squashtm/requirement/${requirementId}`, {}, { timeout: 2 * 60 * 1000 });
export const syncTestCaseToSquash = (projectId, testCaseId) =>
  API.post(`/projects/${projectId}/sync/squashtm/test-case/${testCaseId}`, {}, { timeout: 2 * 60 * 1000 });

// --- Squash TM Delete (supprime proprement le projet depuis Squash TM) ---
export const deleteFromSquash = (projectId) =>
  API.delete(`/projects/${projectId}/sync/squashtm`, { timeout: 2 * 60 * 1000 });

// --- Squash TM Verify (vérifie ce qui est dans Squash TM pour ce projet) ---
export const verifySquashSync = (projectId) =>
  API.get(`/projects/${projectId}/sync/squashtm/verify`);

// --- Squash TM Sync Streaming (SSE) ---
export const syncToSquashStream = (projectId) =>
  fetch(`${BASE_URL}/projects/${projectId}/sync/squashtm/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

// --- Campagnes de test ---
export const getCampaigns = (projectId) => API.get(`/projects/${projectId}/campaigns`);
export const getCampaign = (projectId, campaignId) => API.get(`/projects/${projectId}/campaigns/${campaignId}`);
export const createCampaign = (projectId, data) => API.post(`/projects/${projectId}/campaigns`, data);
export const updateCampaign = (projectId, campaignId, data) => API.put(`/projects/${projectId}/campaigns/${campaignId}`, data);
export const deleteCampaign = (projectId, campaignId) => API.delete(`/projects/${projectId}/campaigns/${campaignId}`);
export const getCampaignTestCases = (projectId, campaignId) => API.get(`/projects/${projectId}/campaigns/${campaignId}/test-cases`);
export const addTestCasesToCampaign = (projectId, campaignId, testCaseIds) => API.post(`/projects/${projectId}/campaigns/${campaignId}/test-cases`, { test_case_ids: testCaseIds });
export const addAllTestCasesToCampaign = (projectId, campaignId) => API.post(`/projects/${projectId}/campaigns/${campaignId}/add-all`);
export const removeTestCaseFromCampaign = (projectId, campaignId, itemId) => API.delete(`/projects/${projectId}/campaigns/${campaignId}/test-cases/${itemId}`);
export const updateCampaignTCStatus = (projectId, campaignId, itemId, data) => API.patch(`/projects/${projectId}/campaigns/${campaignId}/test-cases/${itemId}/status`, data);

// --- Defauts/Anomalies ---
export const getDefects = (projectId, params) => API.get(`/projects/${projectId}/defects`, { params });
export const getDefect = (projectId, defectId) => API.get(`/projects/${projectId}/defects/${defectId}`);
export const createDefect = (projectId, data) => API.post(`/projects/${projectId}/defects`, data);
export const updateDefect = (projectId, defectId, data) => API.put(`/projects/${projectId}/defects/${defectId}`, data);
export const deleteDefect = (projectId, defectId) => API.delete(`/projects/${projectId}/defects/${defectId}`);

// --- Tags ---
export const getProjectTags = (projectId) => API.get(`/projects/${projectId}/tags`);
export const createTag = (projectId, data) => API.post(`/projects/${projectId}/tags`, data);
export const updateTag = (projectId, tagId, data) => API.put(`/projects/${projectId}/tags/${tagId}`, data);
export const deleteTag = (projectId, tagId) => API.delete(`/projects/${projectId}/tags/${tagId}`);
export const setTestCaseTags = (projectId, tcId, tagIds) => API.put(`/projects/${projectId}/test-cases/${tcId}/tags`, { tag_ids: tagIds });

// --- Historique d'execution ---
export const getExecutionHistory = (projectId, testCaseId) => API.get(`/projects/${projectId}/test-cases/${testCaseId}/history`);

// --- Notifications ---
export const getNotifications = () => API.get('/notifications');
export const getUnreadCount = () => API.get('/notifications/unread-count');
export const markNotificationRead = (id) => API.patch(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => API.patch('/notifications/read-all');
export const deleteNotification = (id) => API.delete(`/notifications/${id}`);

// --- Admin: Audit Global ---
export const getAdminAuditLogs = (params) => API.get('/admin/audit/logs', { params });
export const getAdminAuditStats = (params = {}) => API.get('/admin/audit/stats', { params });
export const exportAdminAuditJson = (params) =>
  API.get('/admin/audit/export', { params, responseType: 'blob' });
export const getAdminUsersActivity = (params = {}) => API.get('/admin/audit/users-activity', { params });

// --- Admin: AI Prompt Config ---
export const getAIPromptSections = () => API.get('/admin/ai-prompt');
export const updateAIPromptSection = (sectionKey, data) => API.put(`/admin/ai-prompt/${sectionKey}`, data);
export const getAIPromptHistory = (sectionKey) => API.get(`/admin/ai-prompt/${sectionKey}/history`);
export const restoreAIPromptVersion = (sectionKey, version) => API.post(`/admin/ai-prompt/${sectionKey}/restore/${version}`);

// --- Admin: System Logs ---
export const getSystemLogs = (params) => API.get('/admin/system-logs', { params });
export const getSystemLogsStats = (params = {}) => API.get('/admin/system-logs/stats', { params });
export const exportSystemLogsJson = (params) =>
  API.get('/admin/system-logs/export', { params, responseType: 'blob' });

// Helper: extract safe error message from API errors
export function safeErrorMessage(err, fallback = 'Une erreur est survenue.') {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || fallback;
  if (typeof detail === 'string') return detail.length < 300 ? detail : fallback;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (first && typeof first === 'object' && first.msg) {
      const loc = Array.isArray(first.loc) ? first.loc.join('.') : '';
      return loc ? `${loc}: ${first.msg}` : first.msg;
    }
  }
  return fallback;
}

export default API;
